import type { Translatable } from "app/pages/sales-intelligence/types";

const PREFIX = "si.signal-recommender";
const UPLOAD_PREFIX = `${PREFIX}.upload`;
const CONFIRM_PREFIX = `${PREFIX}.confirm`;
const FILTERS_PREFIX = `${CONFIRM_PREFIX}.filters`;
const LIST_PREFIX = `${PREFIX}.list`;
const TOAST_PREFIX = `${PREFIX}.toast`;

const signalRecommenderI18nKeys = {
    button: {
        uploadCsv: `${PREFIX}.button.upload-csv`,
        analyzeSignalsFromDeals: `${PREFIX}.button.analyze-signals-from-deals`,
        uploadCsvCooldownTooltip: (replacements: { date: string }): Translatable => ({
            key: `${PREFIX}.button.upload-csv-cooldown-tooltip`,
            replacements,
        }),
    },

    banner: {
        title: `${PREFIX}.banner.title`,
        description: `${PREFIX}.banner.description`,
    },

    upload: {
        title: `${UPLOAD_PREFIX}.title`,
        requiredColumnsTitle: `${UPLOAD_PREFIX}.required-columns-title`,
        templateHelp: `${UPLOAD_PREFIX}.template-help`,

        rule: {
            headerRow: `${UPLOAD_PREFIX}.rule.header-row`,
            requiredColumns: `${UPLOAD_PREFIX}.rule.required-columns`,
            matchingRates: `${UPLOAD_PREFIX}.rule.matching-rates`,
            signalTimelines: `${UPLOAD_PREFIX}.rule.signal-timelines`,
            maxCompanies: (replacements: { limit: string }): Translatable => ({
                key: `${UPLOAD_PREFIX}.rule.max-companies`,
                replacements,
            }),
        },

        error: {
            /** The published key is this group's own node — `si.signal-recommender.upload.error`. */
            generic: `${UPLOAD_PREFIX}.error`,
            noDataRows: `${UPLOAD_PREFIX}.error.no-data-rows`,
            tooManyCompanies: (replacements: { count: string; limit: string }): Translatable => ({
                key: `${UPLOAD_PREFIX}.error.too-many-companies`,
                replacements,
            }),
        },

        dropzone: {
            loading: `${UPLOAD_PREFIX}.dropzone.loading`,
            action: `${UPLOAD_PREFIX}.dropzone.action`,
        },

        limitNotice: (replacements: { days: string }): Translatable => ({
            key: `${UPLOAD_PREFIX}.limit-notice`,
            replacements,
        }),
    },

    confirm: {
        explanation: `${CONFIRM_PREFIX}.explanation`,
        enrichDownload: `${CONFIRM_PREFIX}.enrich-download`,
        emailNotice: `${CONFIRM_PREFIX}.email-notice`,
        companiesIdentified: (replacements: { count: string }): Translatable => ({
            key: `${CONFIRM_PREFIX}.companies-identified`,
            replacements,
        }),

        signals: {
            title: `${CONFIRM_PREFIX}.signals.title`,
        },

        filters: {
            searchPlaceholder: `${FILTERS_PREFIX}.search-placeholder`,
            expandAria: `${FILTERS_PREFIX}.expand-aria`,
            collapseAria: `${FILTERS_PREFIX}.collapse-aria`,
            notSelected: `${FILTERS_PREFIX}.not-selected`,

            section: {
                title: `${FILTERS_PREFIX}.section.title`,
            },

            intent: {
                label: `${FILTERS_PREFIX}.intent.label`,
            },

            technology: {
                label: `${FILTERS_PREFIX}.technology.label`,
            },

            error: (replacements: { filter: string }): Translatable => ({
                key: `${FILTERS_PREFIX}.error`,
                replacements,
            }),

            selected: (replacements: { count: number }): Translatable => ({
                key: `${FILTERS_PREFIX}.selected`,
                replacements,
            }),
        },
    },

    list: {
        title: `${LIST_PREFIX}.title`,
        error: `${LIST_PREFIX}.error`,
        companies: `${LIST_PREFIX}.companies`,
        signalsFound: `${LIST_PREFIX}.signals-found`,
        pagination: (replacements: { page: number; total: string }): Translatable => ({
            key: `${LIST_PREFIX}.pagination`,
            replacements,
        }),

        empty: {
            title: `${LIST_PREFIX}.empty.title`,
            subtitle: `${LIST_PREFIX}.empty.subtitle`,
        },

        status: {
            enriching: `${LIST_PREFIX}.status.enriching`,
            stalled: `${LIST_PREFIX}.status.stalled`,
        },

        /**
         * Both carry the "we'll email you" promise inside the sentence rather than appending it as a
         * fragment: the separator and the capitalisation are then the translator's to move.
         */
        startedAgo: (replacements: { elapsed: string }): Translatable => ({
            key: `${LIST_PREFIX}.started-ago`,
            replacements,
        }),

        startedOn: (replacements: { date: string }): Translatable => ({
            key: `${LIST_PREFIX}.started-on`,
            replacements,
        }),

        elapsed: {
            justNow: `${LIST_PREFIX}.elapsed.just-now`,
        },

        metric: {
            pending: `${LIST_PREFIX}.metric.pending`,
            unavailable: `${LIST_PREFIX}.metric.unavailable`,
        },

        download: {
            tooltip: `${LIST_PREFIX}.download.tooltip`,
            failedTooltip: `${LIST_PREFIX}.download.failed-tooltip`,
        },

        refresh: {
            tooltip: `${LIST_PREFIX}.refresh.tooltip`,
        },
    },

    toast: {
        ready: `${TOAST_PREFIX}.ready`,
        error: `${TOAST_PREFIX}.error`,

        /** The request never landed, so no run was created — distinct from a run that failed. */
        uploadError: `${TOAST_PREFIX}.upload-error`,
        downloadError: `${TOAST_PREFIX}.download-error`,
    },
} as const;

export default signalRecommenderI18nKeys;
